import { createReducer, on } from "@ngrx/store";
import { RfidState } from "./rfid.models";
import * as paginationConstants from '../../../shared/constants/pagination.constants';
import * as rfidActions from './rfid.actions';
import { SavingStatus } from "../../../shared/models/list.page.model";
import { patchById } from "../../../shared/utils/list.util";

export const rfidInitialState: RfidState = {
    rfidGroups: [],
    rfidGroupsCurrentFilter: null,
    rfidGroupsPage: null,
    rfidGroupsPagination: {
        pageIndex: 0,
        pageSize: paginationConstants.DefaultPageSize,
    },
    rfidGroupsSaveInProgress: SavingStatus.NotStarted,
    error: null,
    loading: false,
    rfidGroupOverview: null,
    userTokens: [],
    userTokensPage: null,
    userTokensPagination: {
        pageIndex: 0,
        pageSize: paginationConstants.DefaultPageSize
    },
    userTokensCurrentFilter: null,
    userTokensSaveInProgress: SavingStatus.NotStarted,
    empUserTokens: null,
    internalBillingUsers: [],
    internalBillingUsersLoading: false
}

export const rfidReducer = createReducer(
    rfidInitialState,
    on(rfidActions.loadRfidGroupList, (state) => ({
        ...state,
        loading: true
    })),
    on(rfidActions.loadRfidGroupListFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.errorMessage ?? 'Error'
    })),
    on(rfidActions.loadRfidGroupListSuccess, (state, { rfidGroupsResponse }) => ({
        ...state,
        loading: false,
        error: null,
        rfidGroups: rfidGroupsResponse.result ?? [],
        rfidGroupsPage: {
            ...state.rfidGroupsPage,
            totalCount: rfidGroupsResponse.total ?? 0,
            pageIndex: rfidGroupsResponse.pageIndex ?? 0,
            pageSize: rfidGroupsResponse.pageSize ?? 0,
        }
    })),
    on(rfidActions.changeRfidGroupPagination, (state, { pageIndex, pageSize }) => ({
        ...state,
        rfidGroupsPagination: {
            pageIndex,
            pageSize
        }
    })),
    on(rfidActions.updateRfidGroup, (state) => ({
        ...state,
        loading: true,
        rfidGroupsSaveInProgress: SavingStatus.InProgress
    })),
    on(rfidActions.updateRfidGroupSuccess, (state) => ({
        ...state,
        loading: false,
        rfidGroupsSaveInProgress: SavingStatus.Successful
    })),
    on(rfidActions.updateRfidGroupFailure, (state, { error }) => ({
        ...state,
        loading: false,
        rfidGroupsSaveInProgress: SavingStatus.Failure,
        error: error.errorMessage ?? 'ERROR'
    })),
    on(rfidActions.insertNewRfidGroup, (state) => ({
        ...state,
        loading: true,
        rfidGroupsSaveInProgress: SavingStatus.InProgress
    })),
    on(rfidActions.insertNewRfidGroupSuccess, (state) => ({
        ...state,
        loading: false,
        rfidGroupsSaveInProgress: SavingStatus.Successful
    })),
    on(rfidActions.insertNewRfidGroupFailure, (state, { error }) => ({
        ...state,
        loading: false,
        rfidGroupsSaveInProgress: SavingStatus.Failure,
        error: error.errorMessage ?? 'ERROR'
    })),
    on(rfidActions.resetRfidGroupsSavingStatus, (state) => ({
        ...state,
        rfidGroupsSaveInProgress: SavingStatus.NotStarted
    })),
    on(rfidActions.loadRfidGroupOverview, (state) => ({
        ...state,
        loading: true
    })),
    on(rfidActions.loadRfidGroupOverviewSuccess, (state, { result }) => ({
        ...state,
        loading: false,
        rfidGroupOverview: {
            ...result
        }
    })),
    on(rfidActions.loadRfidGroupOverviewFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.errorMessage ?? 'ERROR'
    })),
    on(rfidActions.loadUserTokensRfidList, (state) => ({
        ...state,
        loading: true
    })),
    on(rfidActions.loadUserTokensRfidListSuccess, (state, { userTokens }) => ({
        ...state,
        loading: false,
        userTokens: userTokens.result ?? [],
        userTokensPage: {
            ...state.userTokensPage,
            pageIndex: userTokens.pageIndex ?? 0,
            pageSize: userTokens.pageSize ?? paginationConstants.DefaultPageSize,
            totalCount: userTokens.total ?? 0
        }
    })),
    on(rfidActions.loadUserTokensRfidListFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.errorMessage ?? 'ERROR'
    })),
    on(rfidActions.insertNewUserToken, (state) => ({
        ...state,
        loading: true,
        userTokensSaveInProgress: SavingStatus.InProgress
    })),
    on(rfidActions.insertNewUserTokenSuccess, (state) => ({
        ...state,
        loading: false,
        userTokensSaveInProgress: SavingStatus.Successful
    })),
    on(rfidActions.insertNewUserTokenFailure, (state, { error }) => ({
        ...state,
        loading: false,
        userTokensSaveInProgress: SavingStatus.Failure,
        error: error.errorMessage ?? 'ERROR'
    })),
    on(rfidActions.changeUserTokenPagination, (state, { pageIndex, pageSize }) => ({
        ...state,
        userTokensPagination: {
            ...state.userTokensPagination,
            pageIndex: pageIndex,
            pageSize: pageSize
        }
    })),
    on(rfidActions.blockUserToken, (state) => ({
        ...state,
        loading: true
    })),
    on(rfidActions.blockUserTokenSuccess, (state) => ({
        ...state,
        loading: false
    })),
    on(rfidActions.blockUserTokenFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.errorMessage ?? 'ERROR'
    })),
    on(rfidActions.changeRfidGroupFilter, (state, { filter }) => ({
        ...state,
        rfidGroupsCurrentFilter: {
            ...state.rfidGroupsCurrentFilter,
            groupName: filter.groupName,
            status: filter.status
        }
    })),
    on(rfidActions.changeUserTokenFilter, (state, { filter }) => ({
        ...state,
        userTokensCurrentFilter: {
            ...state.userTokensCurrentFilter,
            isActive: filter.isActive
        }
    })),
    on(rfidActions.changeRfidGroupStatus, (state) => ({
        ...state,
        loading: true
    })),
    on(rfidActions.changeRfidGroupStatusSuccess, (state, { status }) => ({
        ...state,
        loading: false,
        rfidGroupOverview: {
            ...state.rfidGroupOverview!,
            isActive: status
        }
    })),
    on(rfidActions.changeRfidGroupStatusSuccess, (state) => ({
        ...state,
        loading: false
    })),
    on(rfidActions.loadEmpUserTokensList, (state) => ({
        ...state,
        loading: true
    })),
    on(rfidActions.loadEmpUserTokensListFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error: error.errorMessage ?? 'Error'
    })),
    on(rfidActions.loadEmpUserTokensListSuccess, (state, { userTokens }) => ({
        ...state,
        loading: false,
        error: null,
        empUserTokens: userTokens ?? []
    })),
    on(rfidActions.updateUserTokenMetadata, (state) => ({
        ...state,
        loading: true,
        userTokensSaveInProgress: SavingStatus.InProgress
    })),
    on(rfidActions.updateUserTokenMetadataSuccess, (state, { result }) => ({
        ...state,
        userTokens: [
            ... patchById(state.userTokens, result)
        ],
        loading: false,
        
        userTokensSaveInProgress: SavingStatus.Successful
    })),
    on(rfidActions.updateUserTokenMetadataFailure, (state, { error }) => ({
        ...state,
        loading: false,
        userTokensSaveInProgress: SavingStatus.Failure,
        error: error.errorMessage ?? 'ERROR'
    })),
    on(rfidActions.assignUserTokenToCustomer, (state) => ({
        ...state,
        loading: true,
        userTokensSaveInProgress: SavingStatus.InProgress
    })),
    on(rfidActions.loadInternalBillingUsers, (state) => ({
        ...state,
        internalBillingUsersLoading: true
    })),
    on(rfidActions.loadInternalBillingUsersSuccess, (state, { users }) => ({
        ...state,
        internalBillingUsers: users,
        internalBillingUsersLoading: false
    })),
    on(rfidActions.loadInternalBillingUsersFailure, (state) => ({
        ...state,
        internalBillingUsers: [],
        internalBillingUsersLoading: false
    })),
)